import React, { useEffect, useState, useRef } from "react";
import "./profile.scss";
import { SwipeableButton } from "react-swipeable-button";
import { profileAPI, assetAPI } from "../../../apis";
import Loading from "../../../module/app/components/loading/Loding";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";

const Profile = () => {
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    gender: "",
  });

  const [originalData, setOriginalData] = useState(null);
  const navigate = useNavigate();

  const userDetails = JSON.parse(sessionStorage.getItem("userDetails"));
  const userId = userDetails?.user?.id || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasError, setHasError] = useState(false);
  const [resetSwipe, setResetSwipe] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleEditClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (userId) {
          const res = await profileAPI.getProfile(userId);

          if (res?.data && res.status === 200) {
            const { name, email, gender, mobile } = res.data;
            const cleanPhone = mobile?.startsWith("+91")
              ? mobile.slice(3)
              : mobile;

            const fetchedData = {
              name: name || "",
              email: email || "",
              gender: gender || "",
              mobile: cleanPhone || "",
            };

            setFormData(fetchedData);
            setOriginalData(fetchedData); // Save for comparison
          } else {
            setError("Could not load profile. Try again.");
            toast.error("Failed to load profile.");
          }
        }
      } catch (err) {
        console.error("Profile fetch error:", err);
        setError("Could not load profile. Try again.");
        toast.error("Could not load profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const validate = () => {
    const newErrors = {};
    const phone = formData.mobile.trim();

    if (!formData.name.trim()) newErrors.name = "Full name is required.";
    if (!formData.email.trim()) newErrors.email = "Email is required.";
    if (!phone) newErrors.mobile = "Phone number is required.";
    else if (!/^\d+$/.test(phone)) newErrors.mobile = "Phone must be numeric.";
    else if (phone.length !== 10) newErrors.mobile = "Phone must be 10 digits.";
    if (!formData.gender) newErrors.gender = "Gender is required.";

    setFieldErrors(newErrors);
    return newErrors;
  };

  const handleChange = (e) => {
    if (hasError) {
      setHasError(false);
      setResetSwipe((prev) => !prev);
    }

    const { name, value } = e.target;
    if (name === "mobile" && value && !/^\d*$/.test(value)) return;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = async () => {
    const errors = validate();
    if (Object.keys(errors).length) {
      Object.values(errors).forEach((msg) => msg && toast.error(msg));
      setHasError(true);
      return;
    }

    const isChanged = Object.keys(formData).some(
      (key) => formData[key] !== originalData?.[key]
    );

    if (!isChanged && !selectedFile) {
      toast.info("No changes to update.");
      setResetSwipe((prev) => !prev);
      return;
    }

    setSaving(true);
    setError("");

    const finalPayload = {
      ...formData,
      mobile: "+91" + formData.mobile,
    };

    try {
      let assetUploadURL = "";
      let assetRefId = "";

      if (userId) {
        if (selectedFile?.type) {
          const assetRes = await assetAPI.createAsset({
            entityId: userId,
            entityType: "end_user",
            contentType: selectedFile.type,
          });

          if (assetRes?.data && assetRes.status === 200) {
            assetUploadURL = assetRes?.data?.uploadURL || "";
            assetRefId = assetRes?.data?.assetId || "";

            if (assetUploadURL && selectedFile?.type) {
              const uploadRes = await assetAPI.uploadAsset(
                assetUploadURL,
                selectedFile
              );
              if (uploadRes?.status === 200) {
                console.log("Image uploaded successfully!");
              } else {
                console.warn("Asset upload failed");
                toast.error("Profile picture upload failed, Try again!")
              }
            }
          }
        }

        if (assetRefId && selectedFile?.type) {
          finalPayload["profilePictureAssetId"] = assetRefId;
        }

        const { status } = await profileAPI.updateProfile(userId, finalPayload);
        if (status === 200) {
          toast.success("Profile saved successfully!");

          const updatedDetails = {
            ...userDetails,
            user: {
              ...userDetails.user,
              fullName: formData.name,
              email: formData.email,
              mobile: "+91" + formData.mobile,
            },
          };

          sessionStorage.setItem("userDetails", JSON.stringify(updatedDetails));

          setTimeout(() => {
            navigate("/home");
          }, 3000);
        }
      } else {
        toast.error("Update failure, user not found!");
      }
    } catch (err) {
      console.error("Error saving profile:", err);
      setError("Failed to save profile.");
      toast.error("Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="profile-container">
      <button className="back-button" onClick={() => navigate(-1)}>
        ←
      </button>

      <div className="profile-avatar">
        <img
          src={
            selectedFile
              ? URL.createObjectURL(selectedFile) : (userDetails?.user?.profilePictureURL ? userDetails?.user?.profilePictureURL : "/images/image.png")
          }
          alt="Avatar"
        />
        <span className="edit-icon" onClick={handleEditClick}>
          🖊
        </span>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          style={{ display: "none" }}
        />
      </div>

      <div className="form-button-container">
        <form className="profile-form">
          <input
            type="text"
            name="name"
            placeholder="Full Name"
            value={formData.name}
            onChange={handleChange}
          />

          <div className="phone-input">
            <select name="countryCode" value="+91" disabled>
              <option value="+91">+91</option>
            </select>
            <input
              type="tel"
              name="mobile"
              placeholder="Phone Number"
              value={formData.mobile}
              onChange={handleChange}
            />
          </div>

          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
          />

          <select
            name="gender"
            value={formData?.gender}
            onChange={handleChange}
            className="gender-select"
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </form>

        <div className="swipe-to-save">
          <SwipeableButton
            key={resetSwipe}
            onSuccess={handleSubmit}
            onFailure={() => toast.error("Swipe failed. Please try again.")}
            text={saving ? "Saving..." : "Swipe to save"}
            text_unlocked={hasError ? "Failed to save!" : "Saved!"}
            sliderColor={hasError ? "#ff000060" : "#FF6E01"}
            sliderTextColor="#ffffff"
            sliderIconColor="#ffffff"
            background_color="#ffffff60"
            borderRadius={30}
            circle
            autoWidth
            disabled={saving}
          />
        </div>
      </div>

      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
};

export default Profile;
